import { Route, Routes, useLocation } from "react-router-dom";
import DrawerAppBar from "./widgets/NavBar/NavBar";
import FixedBottomNavigation from "./widgets/footer/footer";
import { Home } from "./features/home";
import { About } from "./features/about";
import { Contact } from "./features/contact";
import { CssBaseline, ThemeProvider, Box } from "@mui/material";
import { useThemeMode } from "./theme/ThemeModeContext";
import handleTheme from "./theme/theme";
import { Services } from "./features/services";
import { AuthProvider } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import NotFound from "./widgets/NotFound/NotFound";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import Chatbot from "./widgets/Chatbot/Chatbot";
import WhatsAppButton from "./widgets/WhatsAppButton/WhatsAppButton";
import Spinner from "./shared/ui/Spinner/Spinner";

// Admin & Login are only reached by the site owner — split them out of the
// public marketing bundle so anonymous visitors never download that code.
const Admin = lazy(() => import("./features/Admin/Admin"));
const Login = lazy(() => import("./auth/Login"));

function App() {
  const { darkMode, toggleDarkMode } = useThemeMode();
  const theme = handleTheme(darkMode);
  const { i18n } = useTranslation();
  const location = useLocation();

  const isPrivateRoute = ["/admin", "/login"].includes(location.pathname);

  // Animación de transición de tema
  const themeTransitionVariants = {
    initial: { opacity: 0.8 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  };

  // Agregar clase dark-mode al body para CSS glassmorphism
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
  }, [darkMode]);

  return (
    <>
      {/* SEO Global Metadata */}
      <Helmet>
        <html lang={i18n.language || "en"} />
        {isPrivateRoute && <meta name="robots" content="noindex, nofollow" />}
        {!isPrivateRoute && <meta name="robots" content="index, follow" />}
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta property="og:site_name" content="IT Engine Sparks" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@itenginesparks" />
        <meta name="author" content="IT Engine Sparks" />
        <meta name="geo.region" content="MX-BCN" />
        <meta name="geo.placename" content="Ensenada, Baja California, Mexico" />
        <meta name="geo.position" content="31.8667;-116.5961" />
        <meta name="ICBM" content="31.8667, -116.5961" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["LocalBusiness", "ProfessionalService"],
            "name": "IT Engine Sparks",
            "url": "https://itenginesparks.com",
            "logo": "https://itenginesparks.com/13706.png",
            "image": "https://itenginesparks.com/og-banner.png",
            "description": "Custom web development, React apps, cloud-powered backends and IT consulting for businesses in Mexico and the United States.",
            "telephone": "+52-661-130-1597",
            "email": "itenginesparks@gmail.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Ensenada",
              "addressRegion": "Baja California",
              "addressCountry": "MX"
            },
            "areaServed": [
              { "@type": "Country", "name": "Mexico" },
              { "@type": "Country", "name": "United States" }
            ],
            "founder": {
              "@type": "Person",
              "name": "Carlos A. Avilez J.",
              "jobTitle": "Senior Full Stack Engineer"
            },
            "sameAs": [
              "https://www.facebook.com/profile.php?id=61569313564630",
              "https://www.instagram.com/itenginesparks/"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+52-661-130-1597",
              "contactType": "customer service",
              "areaServed": ["MX", "US"],
              "availableLanguage": ["Spanish", "English"]
            }
          })}
        </script>
      </Helmet>

      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AnimatePresence mode="wait">
          {/* No `key` here on purpose: keying this by darkMode made React
              unmount and remount the entire app (navbar, routes, footer,
              chatbot) on every theme toggle, replaying every mount
              animation and resetting component state (e.g. the navbar's
              scroll-tracked transparency). The CSS transition on the
              body background/color in theme.js already gives the color
              swap a smooth crossfade without any of that. */}
          <motion.div
            variants={themeTransitionVariants}
            initial="initial"
            animate="animate"
            style={{ minHeight: "100vh" }}
          >
            <Box
              sx={{
                minHeight: "100vh",
                background: theme.gradients.hero,
                transition:
                  "background 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
              }}
            >
              <AuthProvider>
                <DrawerAppBar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
                <Suspense fallback={<Spinner />}>
                  <Routes>
                    <Route path="/" element={<Home />}></Route>
                    <Route path="/about" element={<About />}></Route>
                    <Route path="/services" element={<Services />}></Route>
                    <Route path="/contact" element={<Contact />}></Route>
                    <Route path="/login" element={<Login />}></Route>
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <Admin />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <FixedBottomNavigation />

                {/* Botones flotantes globales - aparecen en todas las páginas */}
                <WhatsAppButton />
                <Chatbot />
              </AuthProvider>
            </Box>
          </motion.div>
        </AnimatePresence>
      </ThemeProvider>
    </>
  );
}

export default App;
